import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface ForgotPasswordVerifyOtpProps {
  email: string;
  onOtpVerified: (resetToken: string) => void;
  onBack: () => void;
}

const ForgotPasswordVerifyOtp: React.FC<ForgotPasswordVerifyOtpProps> = ({ email, onOtpVerified, onBack }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiService.requestForgotPasswordOtp(email);
      setSuccess('OTP has been resent to your email');
      setResendCountdown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.verifyForgotPasswordOtp(email, otp);
      setSuccess('OTP verified successfully');
      setTimeout(() => {
        onOtpVerified(response.resetToken);
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center">
            <img
              src={`${process.env.PUBLIC_URL}/logo512.png`}
              alt="Barito FC Logo"
              className="h-14 w-14 object-cover"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify OTP
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter the OTP sent to your email
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
              Enter OTP
            </label>
            <input
              id="otp"
              name="otp"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
              required
              maxLength={4}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-imola-primary focus:border-imola-primary focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
              placeholder="0000"
              disabled={loading}
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
              OTP has been sent to {email}
            </p>
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendCountdown > 0 || resendLoading || loading}
                className="text-sm text-imola-primary hover:text-imola-primary/80 focus:outline-none transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendCountdown > 0 ? (
                  `Resend OTP in ${resendCountdown}s`
                ) : (
                  resendLoading ? 'Sending...' : 'Resend OTP'
                )}
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack}
              className="group relative w-full flex justify-center items-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back
            </button>
            <button
              type="submit"
              disabled={loading || otp.length !== 4}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-imola-primary hover:bg-imola-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordVerifyOtp;

